import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pri-dark to-sec-dark text-white">
      {/* Header with navigation back */}
      <header className="bg-pri-dark/90 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              className="h-10 w-auto"
              src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
              alt="Jingleez"
            />
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="flex justify-center mb-4">
              <ShieldCheckIcon className="h-16 w-16 text-mint-green" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-soft-pink">Privacy Policy</h1>
            <div className="h-1 w-24 bg-mint-green mx-auto mb-6"></div>
            <p className="text-pri-gray text-lg max-w-2xl mx-auto">
              Last updated: April 20, 2025
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-lg max-w-none prose-headings:text-mint-green prose-p:text-pri-gray prose-strong:text-white prose-hr:border-white/10"
          >
            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Introduction</h2>
              <p className="text-pri-gray mb-4">
                At Jingleez, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application.
              </p>
              <p className="text-pri-gray mb-4">
                We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last updated" date of this Privacy Policy. You are encouraged to periodically review this Privacy Policy to stay informed of updates.
              </p>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Information We Collect</h2>
              
              <h3 className="text-xl font-bold mb-2 text-soft-pink">Personal Data</h3>
              <p className="text-pri-gray mb-4">
                We may collect personal information that you provide to us, such as your:
              </p>
              <ul className="list-disc pl-5 mb-4 text-pri-gray">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Date of birth</li>
                <li>Profile picture</li>
                <li>Location data</li>
              </ul>
              
              <h3 className="text-xl font-bold mb-2 text-soft-pink">Usage Data</h3>
              <p className="text-pri-gray mb-4">
                We may also collect information on how the service is accessed and used. This may include:
              </p>
              <ul className="list-disc pl-5 mb-4 text-pri-gray">
                <li>Types of content you view</li>
                <li>Features you use</li>
                <li>Time and frequency of your activities</li>
                <li>Device information</li>
                <li>IP address</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">How We Use Your Information</h2>
              <p className="text-pri-gray mb-4">
                We may use the information we collect for various purposes, including:
              </p>
              <ul className="list-disc pl-5 mb-4 text-pri-gray">
                <li>To provide and maintain our Service</li>
                <li>To notify you about changes to our Service</li>
                <li>To provide customer support</li>
                <li>To gather analysis or valuable information so that we can improve our Service</li>
                <li>To monitor the usage of our Service</li>
                <li>To detect, prevent and address technical issues</li>
                <li>To fulfill any other purpose for which you provide it</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Sharing Your Information</h2>
              <p className="text-pri-gray mb-4">
                We may share information with third parties in certain situations:
              </p>
              <ul className="list-disc pl-5 mb-4 text-pri-gray">
                <li>Business partners, vendors, and subcontractors for the performance of any contract we enter into with them or you</li>
                <li>Analytics providers who help us improve and optimize our service</li>
                <li>If required by law or to respond to legal process</li>
                <li>To protect the rights, property, or safety of Jingleez, our users, or others</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Security</h2>
              <p className="text-pri-gray mb-4">
                We use appropriate technical and organizational measures to protect your personal data. However, no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
              </p>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg mb-8">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Your Rights</h2>
              <p className="text-pri-gray mb-4">
                Depending on your location, you may have certain rights regarding your personal information, such as:
              </p>
              <ul className="list-disc pl-5 mb-4 text-pri-gray">
                <li>Right to access the personal information we hold about you</li>
                <li>Right to request that we correct any personal information we hold about you</li>
                <li>Right to request that we delete any personal information we hold about you</li>
                <li>Right to object to processing of your personal information</li>
                <li>Right to data portability</li>
                <li>Right to withdraw consent where we are relying on consent to process your personal information</li>
              </ul>
              <p className="text-pri-gray">
                To exercise any of these rights, please contact us at <a href="mailto:privacy@jingleez.com" className="text-mint-green hover:text-mint-green/80 transition-colors">contact@jingleez.com</a>.
              </p>
            </div>

            <div className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 rounded-2xl p-8 border border-white/10 shadow-lg">
              <h2 className="text-2xl font-bold mb-4 text-mint-green">Contact Us</h2>
              <p className="text-pri-gray mb-4">
                If you have any questions or concerns about this Privacy Policy, please contact us at:
              </p>
              <p className="text-pri-gray">
                <a href="mailto:privacy@jingleez.com" className="text-mint-green hover:text-mint-green/80 transition-colors">contact@jingleez.com</a>
              </p>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center space-x-8 mb-4">
            <Link to="/terms" className="text-pri-gray hover:text-mint-green transition-colors">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-mint-green">
              Privacy Policy
            </Link>
          </div>
          <p className="text-pri-gray">© {new Date().getFullYear()} Jingleez. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
} 